import { supabase } from '@/integrations/supabase/client';
import type { AdminInfo } from '@/types/superAdmin';
// On utilise l'implémentation commune : la copie locale qui vivait ici avait
// dérivé (contrat `{ data?, authExpired? }` sans champ `error`, 403 traité comme
// une session expirée), ce qui rendait tout échec indétectable côté appelant.
import { invokeWithRetry } from './common/invokeWithRetry';
import i18n from "@/i18n";

/**
 * Admins Management Wrapper
 * Centralizes super admin operations with automatic session refresh
 */

const FEATURE_FLAGS = {
  GET_ADMINS: true,
  CREATE_ADMIN: true,
  UPDATE_ADMIN: true,
  DELETE_ADMIN: true,
};

/**
 * Exécute une mutation d'administration et LÈVE en cas d'échec.
 *
 * Pourquoi lever ? Ces fonctions sont consommées par des `useMutation` : si la
 * promesse se résout, react-query déclenche `onSuccess` et l'écran affiche
 * « … avec succès » alors que rien n'a été fait. Une session expirée ou un
 * refus de droits doivent donc passer par `onError`.
 */
async function invokeAdminMutation(
  functionName: string,
  body: Record<string, any> = {}
): Promise<any> {
  const { data, error, authExpired, forbidden } = await invokeWithRetry(functionName, body);

  if (authExpired) {
    throw new Error(i18n.t("common:errors.sessionExpiredReconnect"));
  }

  if (forbidden) {
    throw new Error(error?.message || i18n.t("common:errors.insufficientRights"));
  }

  if (error) {
    throw new Error(error.message || i18n.t("common:errors.callFailed", { functionName }));
  }

  return data;
}

/**
 * Get all gym admins (excludes super admins)
 */
export async function getAdmins(): Promise<AdminInfo[]> {
  if (!FEATURE_FLAGS.GET_ADMINS) {
    console.log('[getAdmins] Using fallback direct query');
    
    // Fallback: direct query
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const { data, error } = await supabase.functions.invoke('get-admins', {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) throw error;
    return data.admins as AdminInfo[];
  }

  console.log('[getAdmins] Using edge function with retry');

  const result = await invokeWithRetry<{ admins?: AdminInfo[] }>('get-admins', {});

  // Session expirée : la redirection vers /login est déjà lancée, on rend une
  // liste vide plutôt que d'afficher une erreur sur un écran qui disparaît.
  if (result.authExpired) return [];

  // Tout le reste (403 « super admin requis », 500, erreur métier en 200) doit
  // remonter pour que react-query passe en état d'erreur.
  if (result.error) {
    throw new Error(result.error.message || 'Failed to fetch admins');
  }

  if (!result.data?.admins) {
    throw new Error('Failed to fetch admins');
  }

  return result.data.admins as AdminInfo[];
}

/**
 * Create a new admin user
 */
export async function createAdmin(formData: {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  gymIds: string[];
}): Promise<any> {
  if (!FEATURE_FLAGS.CREATE_ADMIN) {
    console.log('[createAdmin] Using fallback direct call');
    
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const { data, error } = await supabase.functions.invoke('create-admin-user', {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
      body: formData,
    });

    if (error) throw error;
    return data;
  }

  console.log('[createAdmin] Using edge function with retry');

  return await invokeAdminMutation('create-admin-user', formData);
}

/**
 * Update admin information
 *
 * ⚠️ La fonction edge s'appelle `update-admin-profile` (il n'existe AUCUNE
 * fonction `update-admin` déployée) et elle lit `{ userId, firstName,
 * lastName, phone }` — pas `{ adminId }`. Avec l'ancien couple nom/corps,
 * chaque édition d'administrateur échouait systématiquement (audit 3.3).
 */
export async function updateAdmin(adminId: string, updateData: {
  firstName?: string;
  lastName?: string;
  phone?: string;
}): Promise<any> {
  const body = {
    userId: adminId,
    firstName: updateData.firstName,
    lastName: updateData.lastName,
    phone: updateData.phone,
  };

  if (!FEATURE_FLAGS.UPDATE_ADMIN) {
    console.log('[updateAdmin] Using fallback direct call');

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const { data, error } = await supabase.functions.invoke('update-admin-profile', {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
      body,
    });

    if (error) throw error;
    return data;
  }

  console.log('[updateAdmin] Using edge function with retry');

  return await invokeAdminMutation('update-admin-profile', body);
}

/**
 * Delete admin user
 *
 * ⚠️ `delete-admin/index.ts` lit `{ userId, deleteAccount }`. L'ancien corps
 * `{ adminId }` provoquait un 400 « userId is required » à chaque suppression,
 * et la case « supprimer aussi le compte » n'était jamais transmise
 * (audit 3.3).
 */
export async function deleteAdmin(adminId: string, deleteAccount = false): Promise<any> {
  const body = { userId: adminId, deleteAccount };

  if (!FEATURE_FLAGS.DELETE_ADMIN) {
    console.log('[deleteAdmin] Using fallback direct call');

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const { data, error } = await supabase.functions.invoke('delete-admin', {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
      body,
    });

    if (error) throw error;
    return data;
  }

  console.log('[deleteAdmin] Using edge function with retry');

  return await invokeAdminMutation('delete-admin', body);
}

/**
 * Assign tenant to admin
 */
export async function assignTenant(userId: string, gymId: string): Promise<any> {
  console.log('[assignTenant] Using edge function with retry');

  return await invokeAdminMutation('manage-admin-assignments', {
    action: 'assign',
    userId,
    gymId,
  });
}

/**
 * Revoke tenant from admin
 */
export async function revokeTenant(userId: string, gymId: string): Promise<any> {
  console.log('[revokeTenant] Using edge function with retry');

  return await invokeAdminMutation('manage-admin-assignments', {
    action: 'revoke',
    userId,
    gymId,
  });
}

